import { createContext, useContext, ReactNode, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useAuthStore } from "@/stores/authStore";
import type { User } from "@/shared/types";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Get access token and user from Zustand store (persisted in localStorage)
  const storeToken = useAuthStore((state) => state.accessToken);
  const storeUser = useAuthStore((state) => state.user);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);
  const setUser = useAuthStore((state) => state.setUser);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const getAuthToken = useAuthStore((state) => state.getAuthToken);
  const [location] = useLocation();

  // Wait for store to hydrate from localStorage before making decisions
  // This prevents redirecting to login before we've loaded persisted auth state
  const isHydrating = typeof window !== 'undefined' && !hasHydrated;

  // If we have user in store, use it immediately (don't block on API)
  // Only fetch from API if we have token but no user, or to refresh
  // IMPORTANT: Don't fetch if we're on login/signup page (prevents loops after logout)
  const isLoginPage = location === '/login' || location === '/signup';
  const hasToken = !!storeToken;
  const shouldFetch = typeof window !== 'undefined' && hasHydrated && hasToken && !storeUser && !isLoginPage;
  
  const { data: fetchedUser = null, isLoading } = useQuery<User | null>({
    queryKey: ["/auth/user"],
    retry: false,
    refetchOnWindowFocus: !isLoginPage, // Don't refetch on login page
    refetchOnMount: !isLoginPage, // Don't refetch on login page
    staleTime: 0,
    enabled: shouldFetch,
    queryFn: async ({ queryKey }) => {
      try {
        const url = queryKey.join("/") as string;
        
        // Use axios client for automatic token refresh on 401
        const { apiClient } = await import("@/lib/axiosClient");
        
        const res = await apiClient.get(url);
        return res.data;
      } catch (error: any) {
        // If 401, token refresh will be handled by axios interceptor
        // If refresh fails, clearAuth will be called by interceptor
        if (error.response?.status === 401) {
          // Token refresh failed or no valid refresh token
          clearAuth();
          return null;
        }
        
        console.error("🔐 Auth error:", error);
        return null;
      }
    },
  });

  // Sync fetched user to store when it changes
  useEffect(() => {
    // Use a flag to track if component is mounted
    let isMounted = true;
    
    if (isMounted && fetchedUser) {
      setUser(fetchedUser);
    } else if (isMounted && fetchedUser === null && storeToken && !isLoading && shouldFetch) {
      // If fetch returned null but we have a token, clear auth (token is invalid)
      clearAuth();
    }
    
    return () => {
      isMounted = false;
    };
  }, [fetchedUser, storeToken, isLoading, shouldFetch, setUser, clearAuth]);

  // Use store user if available (immediate), otherwise use fetched user
  const user = storeUser || fetchedUser;
  
  // Show loading if:
  // 1. Store is still hydrating from localStorage, OR
  // 2. We have a token but no user and we're fetching from API
  // Don't show loading on login/signup pages or if we have no token
  const effectiveIsLoading = isHydrating || (hasHydrated && hasToken && !storeUser && !isLoginPage && isLoading);

  const value = {
    user,
    isLoading: effectiveIsLoading,
    isAuthenticated: !!user && !!storeToken,
    isAdmin: user?.role === 'admin' || user?.role === 'owner',
    isSuperAdmin: user?.role === 'super_admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
